import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { productAPI, customerAPI, invoiceAPI } from '../services/api';
import { Button, Badge } from '../components/Common';
import { useAuth } from '../context/AuthContext';
import { BASE_URL } from '../config';

const BillingPage = () => {
  const { user } = useAuth();
  const billFields = user?.bill_fields || [
    { label: 'GST', type: 'percent', value: '18' },
    { label: 'Discount', type: 'percent', value: '0' },
  ];
  const [fieldValues, setFieldValues] = useState(
    billFields.map(f => ({ ...f, currentValue: f.value }))
  );
  const [products,    setProducts]    = useState([]);
  const [customers,   setCustomers]   = useState([]);
  const [cart,        setCart]        = useState([]);
  const [customerId,  setCustomerId]  = useState('');
  // const [discount,    setDiscount]    = useState(0);
  // const [gst,         setGst]         = useState(18);
  const [selProduct,  setSelProduct]  = useState('');
  const [qty,         setQty]         = useState(1);
  const [unitPrice,   setUnitPrice]   = useState('');
  const [creating,    setCreating]    = useState(false);
  const [lastInvoice, setLastInvoice] = useState(null);
  const [sendWA,      setSendWA]      = useState(true);
  const [sendEmail,   setSendEmail]   = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('paid');

  useEffect(() => {
    productAPI.getAll().then(r => 
      setProducts(r.data.products.filter(p => parseFloat(p.quantity) > 0))
    );
    customerAPI.getAll().then(r => setCustomers(r.data.customers));
  }, []);

  useEffect(() => {
    if (selProduct) {
      const p = products.find(x => String(x.id) === String(selProduct));
      if (p) setUnitPrice(p.sell_price);
    }
  }, [selProduct, products]);

  const addToCart = () => {
    const p = products.find(x => String(x.id) === String(selProduct));
    if (!p) { toast.error('Select a product'); return; }
    if (parseFloat(qty) <= 0) { toast.error('Enter a valid quantity'); return; }
    if (parseFloat(qty) > parseFloat(p.quantity)) {
      toast.error(`Sirf ${p.quantity} ${p.unit} Currently Available`); return;
    }
    const existing = cart.find(i => String(i.product_id) === String(p.id));
    if (existing) {
      setCart(cart.map(i => i.product_id === p.id
        ? { ...i, quantity: parseFloat(i.quantity) + parseFloat(qty) } : i
      ));
    } else {
      setCart([...cart, {
        product_id: p.id, product_name: p.name, unit: p.unit,
        quantity: parseFloat(qty),
        unit_price: parseFloat(unitPrice || p.sell_price),
      }]);
    }
    setQty(1);
    toast.success(`${p.name} Added to cart successfully!`);
  };

  const removeFromCart = (prodId) => 
    setCart(cart.filter(i => i.product_id !== prodId));

  const subtotal = cart.reduce((s, i) => s + i.quantity * i.unit_price, 0);
  
  // Bill fields se calculations
  let runningTotal = subtotal;
  const fieldCalcs = fieldValues.map(f => {
    const val = parseFloat(f.currentValue) || 0;
    let amount = 0;
    if (f.type === 'percent') {
      amount = Math.round(runningTotal * val / 100 * 100) / 100;
    } else {
      amount = val;
    }
    // Discount ke liye minus
    const isDiscount = f.label.toLowerCase().includes('discount');
    if (isDiscount) {
      runningTotal -= amount;
      return { ...f, amount, isDiscount: true };
    } else {
      runningTotal += amount;
      return { ...f, amount, isDiscount: false };
    }
  });
  const total = runningTotal;

  // Backward compat ke liye
  const discField = fieldCalcs.find(f => f.isDiscount);
  const gstField = fieldCalcs.find(f => f.label.toLowerCase().includes('gst'));
  const discount = discField ? parseFloat(discField.currentValue) || 0 : 0;
  const gst = gstField ? parseFloat(gstField.currentValue) || 0 : 18;
  const discAmt = discField ? discField.amount : 0;
  const gstAmt = gstField ? gstField.amount : 0;
  const customer = customers.find(c => String(c.id) === String(customerId));

  const createInvoice = async () => {
    if (cart.length === 0) { toast.error('Cart is empty'); return; }
    setCreating(true);
    try {
      const payload = {
        customer_id: customerId || null,
        items: cart.map(i => ({
          product_id: i.product_id,
          quantity: i.quantity,
          unit_price: i.unit_price,
        })),
        discount_percent: discount,
        gst_percent: gst,
        bill_fields: fieldCalcs.map(f => ({
          label: f.label,
          type: f.type,
          value: f.currentValue,
          amount: f.amount,
          isDiscount: f.isDiscount,
        })),
        payment_status: paymentStatus,
        send_whatsapp: sendWA && !!customer?.whatsapp,
        send_email: sendEmail && !!customer?.email,
      };
      const res = await invoiceAPI.create(payload);
      setLastInvoice(res.data);
      setCart([]); setCustomerId('');
      setFieldValues(billFields.map(f => ({ ...f, currentValue: f.value })));
      toast.success(`✅ ${res.data.invoice?.invoice_number} Created successfully!`);
      if (res.data.whatsapp_sent) toast.success('📱 WhatsApp message sent successfully!');
      if (res.data.email_sent)    toast.success('📧 Email sent successfully!');
      productAPI.getAll().then(r =>
        setProducts(r.data.products.filter(p => parseFloat(p.quantity) > 0))
      );
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invoice generation failed');
    } finally { setCreating(false); }
  };

  const downloadPDF = async (id) => {
    try {
      const res = await invoiceAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${id}.pdf`;
      a.click();
      toast.success('PDF downloaded successfully!');
    } catch { toast.error('PDF download failed.'); }
  };

  return (
    <div className="page">
      <div style={{ marginBottom:24 }}>
        <h1 className="page-title">🧾 Billing</h1>
        <p className="page-sub">Create Invoice • Download PDF • Send via WhatsApp</p>
      </div>

      <div className="two-col">
        {/* LEFT */}
        <div>
          {/* Customer */}
          <div className="card">
            <div className="card-header"><span className="card-title">👤 Customer</span></div>
            <div className="card-body">
              <select className="form-input" value={customerId} onChange={e => setCustomerId(e.target.value)}>
                <option value="">Walk-in Customer (optional)</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>
                ))}
              </select>
              {customer && (
                <div style={{ marginTop:10, padding:10, background:'var(--bg3)', borderRadius:8, fontSize:12, color:'var(--text2)' }}>
                  📱 {customer.whatsapp||'No WhatsApp'} &nbsp;|&nbsp; ✉️ {customer.email||'No Email'}
                </div>
              )}
            </div>
          </div>

          {/* Add Item */}
          <div className="card">
            <div className="card-header"><span className="card-title">➕ Please add an item</span></div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Product *</label>
                <select className="form-input" value={selProduct} onChange={e => setSelProduct(e.target.value)}>
                  <option value="">-- Choose a product --</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.quantity} {p.unit} available)
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input className="form-input" type="number" step="0.01" min="0.01" value={qty} onChange={e => setQty(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Price (₹)</label>
                  <input className="form-input" type="number" step="0.01" min="0" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} />
                </div>
              </div>
              <Button onClick={addToCart} style={{ width:'100%', justifyContent:'center' }}>
                + Please add the item to your cart
              </Button>
            </div>
          </div>

          {/* Cart */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">🛒 Cart ({cart.length})</span>
              {cart.length > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={() => setCart([])}>Clear</button>
              )}
            </div>
            {cart.length === 0 ? (
              <div style={{ padding:24, textAlign:'center', color:'var(--text3)' }}>Your cart is empty</div>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Product</th><th>Qty</th><th>Rate</th><th>Total</th><th></th></tr></thead>
                  <tbody>
                    {cart.map(i => (
                      <tr key={i.product_id}>
                        <td>{i.product_name}</td>
                        <td>{i.quantity} {i.unit}</td>
                        <td>₹{i.unit_price}</td>
                        <td style={{ color:'var(--accent2)', fontWeight:600 }}>
                          ₹{(i.quantity*i.unit_price).toFixed(0)}
                        </td>
                        <td>
                          <button className="btn btn-sm" style={{ color:'var(--danger)', background:'rgba(255,107,107,.1)' }} onClick={() => removeFromCart(i.product_id)}>✕</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT - Invoice Preview */}
        <div>
          <div className="card">
            <div className="card-header"><span className="card-title">📄 Invoice Preview</span></div>
            <div className="card-body">
              <div className="invoice-preview">
                <div style={{ textAlign:'center', marginBottom:16 }}>
                  <div className="inv-brand">⚡ BHAMSA</div>
                  <div style={{ color:'var(--text3)', fontSize:11 }}>
                    {new Date().toLocaleDateString('en-IN')}
                  </div>
                </div>
                <div style={{ marginBottom:14, paddingBottom:10, borderBottom:'1px dashed var(--border)' }}>
                  <div style={{ color:'var(--text2)', fontSize:12 }}>Bill To:</div>
                  <div style={{ fontWeight:600 }}>{customer?.name||'Walk-in Customer'}</div>
                </div>

                {cart.map(i => (
                  <div key={i.product_id} className="invoice-total-row">
                    <span style={{ fontSize:12 }}>{i.product_name} × {i.quantity} {i.unit}</span>
                    <span>₹{(i.quantity*i.unit_price).toFixed(0)}</span>
                  </div>
                ))}

                {cart.length > 0 && (
                  <div style={{ marginTop:12 }}>
                    <div className="invoice-total-row">
                      <span>Subtotal</span><span>₹{subtotal.toFixed(0)}</span>
                    </div>
                    {fieldValues.map((f, index) => (
                      <div key={index}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 0' }}>
                          <span style={{ color:'var(--text2)', fontSize:12, flex:1 }}>
                            {f.label}{f.type === 'percent' ? ' (%)' : ' (₹)'}:
                          </span>
                          <input
                            type="number"
                            className="form-input"
                            style={{ width:70, padding:'4px 8px' }}
                            value={f.currentValue}
                            min={0}
                            onChange={e => {
                              const updated = [...fieldValues];
                              updated[index].currentValue = e.target.value;
                              setFieldValues(updated);
                            }}
                          />
                        </div>
                        {fieldCalcs[index] && fieldCalcs[index].amount > 0 && (
                          <div className="invoice-total-row"
                            style={{ color: fieldCalcs[index].isDiscount ? 'var(--warn)' : 'var(--text2)' }}>
                            <span>{f.label} {f.type === 'percent' ? `(${f.currentValue}%)` : ''}</span>
                            <span>{fieldCalcs[index].isDiscount ? '-' : '+'}₹{fieldCalcs[index].amount.toFixed(0)}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    <div className="inv-grand-total">
                      <span>TOTAL</span><span>₹{total.toFixed(0)}</span>
                    </div>

                    <div style={{ marginBottom:14 }}>
                      <label className="form-label">Payment Status</label>
                      <div style={{ display:'flex', gap:8 }}>
                        {['paid','pending','partial'].map(s => (
                          <button
                            key={s}
                            className={'btn btn-sm ' + (paymentStatus===s ? 'btn-primary' : 'btn-ghost')}
                            onClick={() => setPaymentStatus(s)}
                          >
                            {s==='paid' ? '✅ Paid' : s==='pending' ? '⏳ Pending' : '🔄 Partial'}
                          </button>
                        ))}
                      </div>
                  </div>

                    <Button onClick={createInvoice} loading={creating} variant="success"
                      style={{ width:'100%', justifyContent:'center', padding:12 }}>
                      ✅ Please generate the invoice.
                    </Button>
                  </div>
                )}
                {cart.length === 0 && (
                  <div style={{ color:'var(--text3)', textAlign:'center', padding:20 }}>
                   Please add items to your cart
                  </div>
                )}
              </div>

              {/* Last Invoice */}
              {/* {lastInvoice && (
                <div style={{ marginTop:16, padding:16, background:'var(--bg3)', borderRadius:10 }}>
                  <div style={{ marginBottom:10, fontWeight:600 }}>🎉 Last Invoice</div>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                    <Badge variant="blue">{lastInvoice.invoice?.invoice_number}</Badge>
                    <Badge variant="green">₹{parseFloat(lastInvoice.invoice?.total_amount||0).toFixed(0)}</Badge>
                    {lastInvoice.whatsapp_sent && <Badge variant="green">📱 WA Sent</Badge>}
                    {lastInvoice.email_sent && <Badge variant="green">✉️ Email Sent</Badge>}
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => downloadPDF(lastInvoice.invoice?.id)}>
                    ⬇️ PDF Download
                  </Button>
                </div>
              )} */}

              {lastInvoice && (
                  <div style={{ marginTop:16, padding:16, background:'var(--bg3)', borderRadius:10 }}>
                    <div style={{ marginBottom:10, fontWeight:600 }}>🎉 Last Invoice</div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12 }}>
                      <Badge variant="blue">{lastInvoice.invoice?.invoice_number}</Badge>
                      <Badge variant="green">₹{parseFloat(lastInvoice.invoice?.total_amount||0).toFixed(0)}</Badge>
                      {lastInvoice.whatsapp_sent && <Badge variant="green">📱 WA Sent</Badge>}
                      {lastInvoice.email_sent && <Badge variant="green">✉️ Email Sent</Badge>}
                    </div>
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
                      {/* PDF Download */}
                      <Button variant="secondary" size="sm" onClick={() => downloadPDF(lastInvoice.invoice?.id)}>
                        ⬇️ PDF Download
                      </Button>

                      {/* WhatsApp Share — New Simple Method */}
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => {
                            const inv = lastInvoice.invoice;
                            const custId = inv?.customer_id;
                            const cust = customers.find(c => String(c.id) === String(custId));
                            const custName = cust?.name || 'Customer';
                            const phone = cust?.whatsapp || cust?.phone || '';
                            const pdfUrl = (BASE_URL + '/uploads/invoices/') + inv.invoice_number + '.pdf';
                            const msg = 'Hello ' + custName + ' 👋\n\nYour invoice *#' + inv.invoice_number + '* is ready.\nAmount: *Rs.' + parseFloat(inv.total_amount).toFixed(0) + '*\n\nDownload your bill here:\n' + pdfUrl;
                          
                            
                            const waUrl = 'https://wa.me/91' + phone.replace(/\D/g, '').slice(-10) + '?text=' + encodeURIComponent(msg);
                            window.open(waUrl, '_blank');
                          }}
                      >
                        📱 WhatsApp Share
                      </button>
                      {/* Print Button */}
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={async () => {
                              const inv = lastInvoice.invoice;
                              const pdfUrl = (BASE_URL + '/uploads/invoices/') + inv.invoice_number + '.pdf';
                              
                              // PDF open karo new tab mein — browser print dialog khulega
                              const printWindow = window.open(pdfUrl, '_blank');
                              printWindow.onload = () => {
                                printWindow.print();
                              };
                            }}
                          >
                            🖨️ Print Bill
                          </button>
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPage;